<template>
  <div class="row">
    <div v-if="jurisdiction && !isNewJurisdiction && jurisdiction.state && jurisdiction.state.serviceArea" class="row">
      <div class="col-12">
        <div class="card">
          <div id="map" style="height: 45vh; width: 100%;"></div>
        </div>
      </div>
    </div>

    <EntityForm :entity-id="jurisdictionId" v-model:entity="jurisdiction"
      v-model:editState="isEditingJurisdiction" form-name="Service Jurisdiction" api-url="/api/service-jurisdiction"
      redirect-url="/admin/service-jurisdictions" model-constructor="ServiceJurisdiction" v-bind:show-title=true
      :pre-save="handlePreSave" :post-save="handlePostSave">

      <div v-if="jurisdiction.state">
        <div class="row">
          <div class="col-md-6">
            <div class="mb-3">
              <label for="name" class="form-label">Name <span class="text-danger">*</span></label>
              <input type="text" class="form-control" id="name" v-model="jurisdiction.state.name"
                :disabled="!isEditingJurisdiction" :readonly="!isEditingJurisdiction" required>
              <div id="name-error" class="invalid-feedback d-none">Please enter a name.</div>
            </div>

            <div class="mb-3">
              <label for="description" class="form-label">Description</label>
              <textarea class="form-control" id="description" v-model="jurisdiction.state.description" rows="3"
                :disabled="!isEditingJurisdiction" :readonly="!isEditingJurisdiction"></textarea>
            </div>

            <!-- Deliberately no "Service Area Allowance" input here any more (#197). That field wrote
                 ServiceJurisdiction.serviceAreaAllowance, which no dispatch code has ever read — it
                 looked like a distance bound and bounded nothing. Leaving it beside the real one below
                 would guarantee an admin tunes the box that does nothing. The column still exists and
                 still round-trips through the API; only the misleading control is gone. -->
            <div class="mb-3">
              <label for="maxPickupDistanceMeters" class="form-label">Max Pickup Distance</label>
              <div class="input-group has-validation">
                <input type="number" class="form-control" id="maxPickupDistanceMeters"
                  v-model.number="jurisdiction.state.maxPickupDistanceMeters" min="500" max="15000" step="100"
                  :disabled="!isEditingJurisdiction" :readonly="!isEditingJurisdiction">
                <span class="input-group-text">m</span>
                <div id="maxPickupDistanceMeters-error" class="invalid-feedback d-none">Please enter a whole number of metres between 500 and 15000.</div>
              </div>
              <small class="form-text text-muted">How far a driver may be from the pickup, by road, to be
                sent the ride. Defaults to 5000 m. Every kilometre driven to reach a rider is insured and
                earns nothing, so a tighter radius trades unmatched requests — which cost nothing — for
                less uncharged driving.</small>
              <!-- Advisory only. It must never set .is-invalid: EntityForm blocks save on an invalid
                   field, and widening the radius in a thin market is legitimate.
                   Bound to a local computed rather than reaching into jurisdiction.validations: that
                   object holds computed refs, and whether a template unwraps them through a nested
                   reactive proxy is subtle enough that the first attempt shipped a `.value` that read
                   undefined and rendered nothing. A top-level setup binding is unwrapped for certain. -->
              <small v-if="isPickupRadiusAboveAdvisory" class="form-text text-warning d-block">
                Above 8000 m the deadhead cost per ride rises sharply. Widen this only against a measured
                unmatched-request rate.
              </small>
            </div>
          </div>

          <div class="col-md-6">
            <div class="mb-3">
              <label for="serviceArea" class="form-label">Service Area (Geo Zone)</label>
              <select class="form-select" id="serviceArea" v-model="jurisdiction.state.serviceArea"
                :disabled="!isEditingJurisdiction">
                <option :value="null">-- Select Service Area --</option>
                <option v-for="zone in geoZones" :key="zone.id" :value="zone">
                  {{ zone.name }}
                </option>
              </select>
              <small class="form-text text-muted">Select a geo zone to define the service area</small>
            </div>

            <div class="mb-3">
              <label for="maxDrivers" class="form-label">Maximum Drivers</label>
              <input type="number" class="form-control" id="maxDrivers" v-model="jurisdiction.state.maxDrivers"
                min="0" step="1" :disabled="!isEditingJurisdiction" :readonly="!isEditingJurisdiction">
              <small class="form-text text-muted">Maximum number of drivers allowed in this jurisdiction (leave empty for unlimited)</small>
            </div>

            <div class="mb-3">
              <label for="commissionPercentage" class="form-label">Commission Percentage (%)</label>
              <div class="input-group">
                <input type="number" class="form-control" id="commissionPercentage"
                  v-model="jurisdiction.state.commissionPercentage" min="0" max="100" step="0.01"
                  :disabled="!isEditingJurisdiction" :readonly="!isEditingJurisdiction">
                <span class="input-group-text">%</span>
              </div>
              <small class="form-text text-muted">Platform commission deducted from driver earnings per ride (excludes tips)</small>
              <div id="commissionPercentage-error" class="invalid-feedback d-none">Please enter a valid percentage between 0 and 100.</div>
            </div>

            <div class="mb-3">
              <label for="pointsPerDollar" class="form-label">Asher Points Rate</label>
              <div class="input-group">
                <input type="number" class="form-control" id="pointsPerDollar"
                  v-model.number="jurisdiction.state.pointsPerDollar" min="0" max="1" step="0.01"
                  :disabled="!isEditingJurisdiction" :readonly="!isEditingJurisdiction">
                <span class="input-group-text">pts/$</span>
              </div>
              <small class="form-text text-muted">Points earned per eligible dollar — the all-in amount charged to the
                rider (sales tax included) less the platform fee and any loyalty discount, excluding tips. Defaults to
                1.00, which is also the maximum: at a 2x promo that is already 2% of eligible fare, the margin
                ceiling.</small>
              <div id="pointsPerDollar-error" class="invalid-feedback d-none">Please enter a rate between 0 and 1.00.</div>
            </div>

            <div class="mb-3">
              <label for="signupBonusPoints" class="form-label">Rider Signup Bonus</label>
              <div class="input-group">
                <input type="number" class="form-control" id="signupBonusPoints"
                  v-model.number="jurisdiction.state.signupBonusPoints" min="0" :max="MAX_SIGNUP_BONUS_POINTS" step="1"
                  :disabled="!isEditingJurisdiction" :readonly="!isEditingJurisdiction">
                <span class="input-group-text">pts</span>
              </div>
              <small class="form-text text-muted">Points credited once to every rider who signs up while the promo
                below is running. Set to 0 to pay no signup bonus. Defaults to 250 (worth $2.50).</small>
              <div id="signupBonusPoints-error" class="invalid-feedback d-none">Please enter a whole number of points between 0 and {{ MAX_SIGNUP_BONUS_POINTS }}.</div>
            </div>

            <div class="row">
              <div class="col-md-6 mb-3">
                <label for="signupBonusStartsOn" class="form-label">Signup Promo Starts</label>
                <input type="datetime-local" class="form-control" id="signupBonusStartsOn"
                  v-model="signupBonusStartsOnInput"
                  :disabled="!isEditingJurisdiction" :readonly="!isEditingJurisdiction">
                <small class="form-text text-muted">Leave empty to run the promo immediately.</small>
              </div>
              <div class="col-md-6 mb-3">
                <label for="signupBonusEndsOn" class="form-label">Signup Promo Ends</label>
                <input type="datetime-local" class="form-control" id="signupBonusEndsOn"
                  v-model="signupBonusEndsOnInput"
                  :disabled="!isEditingJurisdiction" :readonly="!isEditingJurisdiction">
                <small class="form-text text-muted">Leave empty to run the promo with no end date.</small>
                <div id="signupBonusEndsOn-error" class="invalid-feedback d-none">The promo must end after it starts.</div>
              </div>
            </div>

            <div class="row">
              <div class="col-md-6 mb-3">
                <label for="idleWarningMinutes" class="form-label">Idle Warning</label>
                <!-- The feedback div lives inside the input-group, and the input-group carries
                     has-validation: Bootstrap only reveals .invalid-feedback through the sibling
                     combinator .is-invalid ~ .invalid-feedback, and .is-invalid goes on the <input>.
                     Left outside, the admin gets a red border and no reason for it — on the one field
                     whose misconfiguration silently stops every driver here from being prompted. -->
                <div class="input-group has-validation">
                  <input type="number" class="form-control" id="idleWarningMinutes"
                    v-model.number="jurisdiction.state.idleWarningMinutes" min="1" step="1"
                    :disabled="!isEditingJurisdiction" :readonly="!isEditingJurisdiction">
                  <span class="input-group-text">min</span>
                  <div id="idleWarningMinutes-error" class="invalid-feedback d-none">Please enter a whole number of minutes, at least 1 and at most 720.</div>
                </div>
                <small class="form-text text-muted">How long a driver may stay online with no trip before
                  the app asks whether they are still driving. Defaults to 30.</small>
              </div>
              <div class="col-md-6 mb-3">
                <label for="idleAutoOfflineMinutes" class="form-label">Idle Auto-Offline</label>
                <div class="input-group has-validation">
                  <input type="number" class="form-control" id="idleAutoOfflineMinutes"
                    v-model.number="jurisdiction.state.idleAutoOfflineMinutes" min="1" step="1"
                    :disabled="!isEditingJurisdiction" :readonly="!isEditingJurisdiction">
                  <span class="input-group-text">min</span>
                  <div id="idleAutoOfflineMinutes-error" class="invalid-feedback d-none">Auto-offline must be a whole number of minutes, at most 720, and more than one minute above the idle warning.</div>
                </div>
                <small class="form-text text-muted">How long a driver may stay online with no trip before
                  being set offline automatically. Must be more than a minute above the warning above, or
                  drivers here are taken offline without ever being asked. Defaults to 45.</small>
              </div>
            </div>

            <hr class="my-4">
            <h6 class="text-muted mb-3">Referral Rewards</h6>

            <div class="row">
              <div class="col-md-6 mb-3">
                <label for="referralDriverDiscountPercent" class="form-label">Driver Referral Discount</label>
                <div class="input-group has-validation">
                  <input type="number" class="form-control" id="referralDriverDiscountPercent"
                    v-model.number="jurisdiction.state.referralDriverDiscountPercent"
                    :min="MIN_REFERRAL_DISCOUNT_PERCENT" :max="MAX_REFERRAL_DISCOUNT_PERCENT" step="1"
                    :disabled="!isEditingJurisdiction" :readonly="!isEditingJurisdiction">
                  <span class="input-group-text">%</span>
                  <div id="referralDriverDiscountPercent-error" class="invalid-feedback d-none">Please enter a whole percentage between {{ MIN_REFERRAL_DISCOUNT_PERCENT }} and {{ MAX_REFERRAL_DISCOUNT_PERCENT }}.</div>
                </div>
                <small class="form-text text-muted">How much a referrer saves on their next subscription
                  week when someone they referred qualifies. Defaults to 50%. To stop paying driver
                  referral rewards here, set the cap beside this to 0 — a 0% discount would leave drivers
                  holding a reward that can never be applied.</small>
              </div>
              <div class="col-md-6 mb-3">
                <label for="referralDriverMaxRedemptions" class="form-label">Driver Referral Cap</label>
                <div class="input-group has-validation">
                  <input type="number" class="form-control" id="referralDriverMaxRedemptions"
                    v-model.number="jurisdiction.state.referralDriverMaxRedemptions"
                    min="0" :max="MAX_REFERRAL_REWARD_CAP" step="1"
                    :disabled="!isEditingJurisdiction" :readonly="!isEditingJurisdiction">
                  <span class="input-group-text">rewards</span>
                  <div id="referralDriverMaxRedemptions-error" class="invalid-feedback d-none">Please enter a whole number between 0 and {{ MAX_REFERRAL_REWARD_CAP }}.</div>
                </div>
                <small class="form-text text-muted">Discounted weeks one driver can earn from referrals.
                  Defaults to 4. Lowering this stops new rewards being granted; it does not revoke a
                  reward a driver has already earned.</small>
              </div>
            </div>

            <div class="row">
              <div class="col-md-4 mb-3">
                <label for="referralRiderFirstRideReferrerPoints" class="form-label">Rider Referrer Bonus</label>
                <div class="input-group has-validation">
                  <input type="number" class="form-control" id="referralRiderFirstRideReferrerPoints"
                    v-model.number="jurisdiction.state.referralRiderFirstRideReferrerPoints"
                    min="0" :max="MAX_REFERRAL_RIDER_POINTS" step="1"
                    :disabled="!isEditingJurisdiction" :readonly="!isEditingJurisdiction">
                  <span class="input-group-text">pts</span>
                  <div id="referralRiderFirstRideReferrerPoints-error" class="invalid-feedback d-none">Please enter a whole number of points between 0 and {{ MAX_REFERRAL_RIDER_POINTS }}.</div>
                </div>
                <small class="form-text text-muted">Paid to the referrer on their friend's first ride.
                  Defaults to 100.</small>
              </div>
              <div class="col-md-4 mb-3">
                <label for="referralRiderFirstRideReferredPoints" class="form-label">New Rider Bonus</label>
                <div class="input-group has-validation">
                  <input type="number" class="form-control" id="referralRiderFirstRideReferredPoints"
                    v-model.number="jurisdiction.state.referralRiderFirstRideReferredPoints"
                    min="0" :max="MAX_REFERRAL_RIDER_POINTS" step="1"
                    :disabled="!isEditingJurisdiction" :readonly="!isEditingJurisdiction">
                  <span class="input-group-text">pts</span>
                  <div id="referralRiderFirstRideReferredPoints-error" class="invalid-feedback d-none">Please enter a whole number of points between 0 and {{ MAX_REFERRAL_RIDER_POINTS }}.</div>
                </div>
                <small class="form-text text-muted">Paid to the referred rider on their own first ride.
                  Defaults to 300.</small>
              </div>
              <div class="col-md-4 mb-3">
                <label for="referralRiderFiveRidesReferrerPoints" class="form-label">Five-Ride Bonus</label>
                <div class="input-group has-validation">
                  <input type="number" class="form-control" id="referralRiderFiveRidesReferrerPoints"
                    v-model.number="jurisdiction.state.referralRiderFiveRidesReferrerPoints"
                    min="0" :max="MAX_REFERRAL_RIDER_POINTS" step="1"
                    :disabled="!isEditingJurisdiction" :readonly="!isEditingJurisdiction">
                  <span class="input-group-text">pts</span>
                  <div id="referralRiderFiveRidesReferrerPoints-error" class="invalid-feedback d-none">Please enter a whole number of points between 0 and {{ MAX_REFERRAL_RIDER_POINTS }}.</div>
                </div>
                <small class="form-text text-muted">Paid to the referrer once their friend reaches five
                  paid rides. Defaults to 400.</small>
              </div>
            </div>

            <div class="row">
              <div class="col-md-6 mb-3">
                <label for="referralRiderMaxRewardedReferrals" class="form-label">Rider Referral Cap</label>
                <div class="input-group has-validation">
                  <input type="number" class="form-control" id="referralRiderMaxRewardedReferrals"
                    v-model.number="jurisdiction.state.referralRiderMaxRewardedReferrals"
                    min="0" :max="MAX_REFERRAL_REWARD_CAP" step="1"
                    :disabled="!isEditingJurisdiction" :readonly="!isEditingJurisdiction">
                  <span class="input-group-text">referrals</span>
                  <div id="referralRiderMaxRewardedReferrals-error" class="invalid-feedback d-none">Please enter a whole number between 0 and {{ MAX_REFERRAL_REWARD_CAP }}.</div>
                </div>
                <small class="form-text text-muted">Referred riders one rider can be rewarded for.
                  Defaults to 10.</small>
              </div>
            </div>

            <div class="mb-3">
              <label for="active" class="form-label">Status</label>
              <div class="form-check form-switch">
                <input class="form-check-input" type="checkbox" id="active" v-model="jurisdiction.state.active"
                  :disabled="!isEditingJurisdiction">
                <label class="form-check-label" for="active">
                  {{ jurisdiction.state.active ? 'Active' : 'Inactive' }}
                </label>
              </div>
            </div>
          </div>
        </div>

      </div>
    </EntityForm>

    <div v-if="!isNewJurisdiction">
      <Suspense>
        <FareConfigurationComponent :service-jurisdiction-id="jurisdictionId"/>
      </Suspense>

      <div class="col-xl-12 xl-100 box-col-6 mt-4">
        <div class="card">
          <div class="card-header d-flex justify-content-between align-items-center">
            <h5>Drivers</h5>
            <span v-if="jurisdiction.state && jurisdiction.state.maxDrivers" class="badge bg-info">
              Max: {{ jurisdiction.state.maxDrivers }}
            </span>
          </div>

          <div class="card-body social-list filter-cards-view">
            <div>
              <DataTable
                :ajax="{ url: `/api/datatable/sj-all-drivers/${jurisdictionId}` }"
                ref="driverDataTable" class="table-responsive display" :columns="driverColumns"/>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, watch, computed, defineProps } from 'vue';
import EntityForm from './EntityForm.vue';
import { toDatetimeLocal, fromDatetimeLocal } from '../utility/datetimeLocal';
import {
  MAX_SIGNUP_BONUS_POINTS,
  WARN_ABOVE_PICKUP_DISTANCE_METERS,
  MIN_REFERRAL_DISCOUNT_PERCENT,
  MAX_REFERRAL_DISCOUNT_PERCENT,
  MAX_REFERRAL_REWARD_CAP,
  MAX_REFERRAL_RIDER_POINTS,
} from '../modelTemplates/ServiceJurisdiction';
import DataTable from 'datatables.net-vue3';
import DataTablesCore from 'datatables.net-bs5';
import FareConfigurationComponent from './FareConfigurationComponent.vue';
import { userDetailsWithThumbnail, driverStatus, zonedDateTime } from '../utility/DatatableRenderer';
import {newEntityId} from '../globals.js';

DataTable.use(DataTablesCore);

const props = defineProps({
  googleMapsApiKey: {
    type: String,
    required: true
  },
  jurisdictionId: {
    type: String,
    default: null
  }
});

const jurisdiction = ref({});
const geoZones = ref([]);
const isNewJurisdiction = computed(() => props.jurisdictionId === 'add' || props.jurisdictionId === newEntityId);
const isEditingJurisdiction = ref(false);

// The >8 km advisory from #197. Derived here, not read off jurisdiction.validations, so the template
// binds a plain boolean — see the comment on the element itself.
const isPickupRadiusAboveAdvisory = computed(() =>
  Number(jurisdiction.value?.state?.maxPickupDistanceMeters) > WARN_ABOVE_PICKUP_DISTANCE_METERS);
const driverDataTable = ref(null);

// The promo window is stored as ISO-8601 (what the API sends and expects) but a datetime-local input
// only accepts 'YYYY-MM-DDTHH:mm' in local time, so the two are proxied rather than bound directly.
// '' rather than null for a cleared input: the update endpoint reads absent/null as "leave unchanged"
// like every other field, and an explicit blank as "clear this date".
//
// The setters guard on state the same way the getters do. Today they cannot fire before EntityForm
// populates it -- the inputs live under v-if="jurisdiction.state" -- but that invariant sits 190 lines
// away in the template, and an asymmetric pair invites someone to "tidy" the getter's guard away.
const signupBonusStartsOnInput = computed({
  get: () => toDatetimeLocal(jurisdiction.value.state?.signupBonusStartsOn),
  set: (value) => {
    if (jurisdiction.value.state) jurisdiction.value.state.signupBonusStartsOn = fromDatetimeLocal(value, '');
  }
});

const signupBonusEndsOnInput = computed({
  get: () => toDatetimeLocal(jurisdiction.value.state?.signupBonusEndsOn),
  set: (value) => {
    if (jurisdiction.value.state) jurisdiction.value.state.signupBonusEndsOn = fromDatetimeLocal(value, '');
  }
});

const driverColumns = [
  {
    title: "Driver", data: "name", render: (data, type, row) => {
      return userDetailsWithThumbnail(data, row, `/admin/independent-driver/${row.id}`);
    }
  },
  { data: "email", title: "Email" },
  { data: "phoneNumber", title: 'Phone Number' },
  { data: "driverStatus", title: 'Status', render: driverStatus },
  { data: "joinedOn", title: 'Joined At', render: zonedDateTime },
  {
    title: 'Actions', orderable: false, searchable: false, render: (data, type, row) => {
      return `<a class="btn btn-outline-primary" data-id="${row.id}"
              href="/admin/independent-driver/${row.id}">
              Details <i class="fa-solid fa-arrow-up-right-from-square"></i> </a>`;
    }
  }
];

let map = null;
let polygon = null;

const fetchGeoZones = async () => {
  try {
    const response = await fetch('/api/service-area-zones');
    if (response.ok) {
      geoZones.value = await response.json();
    }
  } catch (error) {
    console.error('Error fetching geo zones:', error);
  }
};

const initMap = () => {
  if (!jurisdiction.value || !jurisdiction.value.state || !jurisdiction.value.state.serviceArea) return;

  setTimeout(() => {
    const mapElement = document.getElementById('map');
    if (!mapElement) return;

    map = new google.maps.Map(mapElement, {
      center: { lat: 0, lng: 0 },
      zoom: 10,
      mapTypeId: 'roadmap'
    });

    if (jurisdiction.value.state.serviceArea && jurisdiction.value.state.serviceArea.boundaryWkt) {
      drawPolygon(jurisdiction.value.state.serviceArea.boundaryWkt);
    }
  }, 100);
};

const drawPolygon = (boundaryJson) => {
  if (!map) return;

  if (polygon) {
    polygon.setMap(null);
  }

  try {
    let coordinates = [];
    try {
      const boundaryData = JSON.parse(boundaryJson);
      if (Array.isArray(boundaryData)) {
        coordinates = boundaryData.map(coord => ({
          lat: parseFloat(coord.lat || coord.latitude),
          lng: parseFloat(coord.lng || coord.longitude)
        }));
      }
    } catch (e) {
      const wktMatch = boundaryJson.match(/POLYGON\s*\(\((.*)\)\)/i);
      if (wktMatch && wktMatch[1]) {
        coordinates = wktMatch[1].split(',').map(pair => {
          const [lng, lat] = pair.trim().split(' ').map(parseFloat);
          return { lat, lng };
        });
      }
    }

    if (coordinates.length === 0) {
      console.error('Invalid boundary format');
      return;
    }

    polygon = new google.maps.Polygon({
      paths: coordinates,
      strokeColor: '#4285F4',
      strokeOpacity: 0.8,
      strokeWeight: 2,
      fillColor: '#4285F4',
      fillOpacity: 0.35
    });

    polygon.setMap(map);

    const bounds = new google.maps.LatLngBounds();
    coordinates.forEach(coord => {
      bounds.extend(coord);
    });
    map.fitBounds(bounds);

  } catch (error) {
    console.error('Error drawing polygon:', error);
  }
};

const handlePreSave = () => {
  if (jurisdiction.value && jurisdiction.value.state) {
    if (jurisdiction.value.state.serviceArea && jurisdiction.value.state.serviceArea.id) {
      jurisdiction.value.state.geoZoneId = jurisdiction.value.state.serviceArea.id;
    } else {
      jurisdiction.value.state.geoZoneId = null;
    }
  }
};

const handlePostSave = (savedData) => {
  if (map && savedData.serviceArea && savedData.serviceArea.boundaryWkt) {
    drawPolygon(savedData.serviceArea.boundaryWkt);
  } else {
    initMap();
  }
};

const loadGoogleMapsApi = () => {
  const script = document.createElement('script');
  script.src = `https://maps.googleapis.com/maps/api/js?key=${props.googleMapsApiKey}&loading=async&callback=initGoogleMaps`;
  script.async = true;
  script.defer = true;
  document.head.appendChild(script);

  window.initGoogleMaps = () => {
    if (!isNewJurisdiction.value && jurisdiction.value && jurisdiction.value.state) {
      initMap();
    }
  };
};

watch(() => jurisdiction.value?.state?.serviceArea, (newValue) => {
  if (map && newValue && newValue.boundaryWkt) {
    drawPolygon(newValue.boundaryWkt);
  }
});

onMounted(() => {
  fetchGeoZones();
  loadGoogleMapsApi();
});
</script>
